import { countOrNull, durationOrNull, isRecord } from "./service-json";
import type { LivePeerAnnouncement, LivePeerInfo } from "./session-state";
import { isDefaultFraming, type CameraFramings } from "./video/camera-framing";

/**
 * Les commandes de séance, adressées au service de temps.
 *
 * # Pourquoi elles passent par lui
 *
 * Une pause, une reprise, un déplacement, le mode Jam, le lead, les délais de
 * restitution : ce sont les seuls états sur lesquels deux participants peuvent
 * être en désaccord. Le service en est l'unique autorité — il applique, il
 * publie dans la métadonnée de salle ce que toute la salle doit savoir, et
 * LiveKit diffuse. Un client qui annoncerait lui-même risquerait d'écrire sans
 * annoncer, ou l'inverse. La règle de décision : si deux participants peuvent
 * en être en désaccord, ça passe par ici ; sinon, par le canal de données.
 *
 * # La position voyage, l'instant aussi
 *
 * `positionMs` est la position visée, `atMs` l'instant **local** auquel elle
 * était vraie — traduit sur l'horloge du service par l'écart mesuré. Les deux,
 * parce qu'ils définissent une droite : ancrée à la réception, une reprise
 * décalerait l'émetteur du temps de montée du réseau, et chacun repartirait
 * d'un endroit différent.
 */

export type SessionCommand = "pause" | "resume" | "seek";

/**
 * Envoie une commande. Rend `false` sans lever — le service peut être absent
 * ou refuser, et la séance doit survivre à ça : les notes circulent en pair à
 * pair, seule la position partagée se dégrade.
 *
 * # Pourquoi l'échec se journalise ici
 *
 * Rendre `false` suffisait à l'appelant et ne suffisait à personne d'autre :
 * une séance restée figée parce que la reprise n'était jamais partie ne laissait
 * aucune trace — ni ici, ni côté service, qui n'avait rien reçu. Le motif se
 * connaît à cet endroit précis, et nulle part ailleurs : refus du service avec
 * son code, ou requête qui n'est jamais partie.
 */
export async function sendSessionCommand(
  clockUrl: string,
  room: string,
  token: string,
  command: SessionCommand,
  positionMs: number,
  atMs: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(
    clockUrl,
    `/sessions/${room}/${command}`,
    token,
    { position_ms: positionMs, at_ms: Math.round(atMs) },
    `commande ${command}`,
    fetchImpl,
  );
}

/**
 * Change le tempo de la séance, pour tout le monde, à partir de maintenant.
 *
 * Une commande comme la pause, avec en plus le **tick** de la position
 * envoyée : le service ne connaît pas les ticks, et c'est ce couple position,
 * tick qui laisse chaque participant reconstruire la même grille, même quand
 * le service a oublié les plus anciennes marques. Voir
 * `lib/live/session-tempo.ts`.
 *
 * Même discipline d'échec que `sendSessionCommand` : `false` sans lever, motif
 * dans la console, et l'état revient par la métadonnée.
 */
export async function sendSessionTempo(
  clockUrl: string,
  room: string,
  token: string,
  tempo: { bpm: number; tick: number },
  positionMs: number,
  atMs: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(
    clockUrl,
    `/sessions/${room}/tempo`,
    token,
    { position_ms: positionMs, at_ms: Math.round(atMs), tempo_bpm: tempo.bpm, tick: tempo.tick },
    "commande tempo",
    fetchImpl,
  );
}

/**
 * Change le chiffrage de la séance, pour tout le monde.
 *
 * Une commande comme le tempo, sans tick : le chiffrage n'a pas d'historique,
 * il vaut pour toute la grille. La position voyage quand même, parce que toute
 * commande réancre la droite de la séance.
 *
 * Même discipline d'échec que `sendSessionCommand` : `false` sans lever, motif
 * dans la console, et l'état revient par la métadonnée.
 */
export async function sendSessionSignature(
  clockUrl: string,
  room: string,
  token: string,
  signature: { beats: number; unit: number },
  positionMs: number,
  atMs: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(
    clockUrl,
    `/sessions/${room}/signature`,
    token,
    {
      position_ms: positionMs,
      at_ms: Math.round(atMs),
      signature: { beats: signature.beats, unit: signature.unit },
    },
    "commande chiffrage",
    fetchImpl,
  );
}

/**
 * Ouvre la prise suivante : la frontière entre deux traversées d'un même
 * enregistrement.
 *
 * # Pourquoi c'est une commande de transport
 *
 * Parce qu'elle porte une **position**. Quelqu'un qui s'enregistre seul
 * recommence : il joue, se trompe, reprend du début. Ce qui découpe
 * l'enregistrement, c'est l'endroit de la musique où il a repris — pas l'instant
 * où la requête est arrivée au service, ni celui où la métadonnée est repartie
 * vers la salle. Le service amortit ses écritures ; entre le geste et la
 * publication il y a plusieurs centaines de millisecondes, et un découpage posé
 * là tomberait au milieu d'une note. `positionMs` et `atMs` voyagent donc comme
 * pour une pause, et c'est le couple qui date la frontière.
 *
 * # Pourquoi le numéro de la prise en cours part avec
 *
 * C'est la seule commande du lot qui **incrémente** au lieu de poser une
 * valeur : une pause rejouée laisse la séance en pause, mais une prise rejouée
 * en sauterait une — et une prise sautée, c'est une frontière posée là où
 * personne n'a repris. On annonce donc ce qu'on voit (`fromTake`, lu dans l'état
 * publié) et le service ne franchit la frontière que depuis là : un envoi parti
 * deux fois finit comme s'il n'était parti qu'une fois. Un numéro qui ne
 * correspond plus vaut 409, et l'état suivant remettra l'appelant d'accord.
 *
 * Réservé à l'interprète : le service refuse (403) une prise demandée par
 * quelqu'un d'autre que le leader, parce qu'on ne découpe pas l'enregistrement
 * d'un autre.
 *
 * Même discipline d'échec que `sendSessionCommand` : `false` sans lever, motif
 * dans la console, et la réponse n'est pas lue — le compteur revient par la
 * métadonnée, et c'est lui que le panneau affiche.
 */
export async function sendNextTake(
  clockUrl: string,
  room: string,
  token: string,
  fromTake: number,
  positionMs: number,
  atMs: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(
    clockUrl,
    `/sessions/${room}/take`,
    token,
    { position_ms: positionMs, at_ms: Math.round(atMs), from_take: fromTake },
    "nouvelle prise",
    fetchImpl,
  );
}

/**
 * Allume ou éteint le mode Jam, pour toute la séance.
 *
 * # Pourquoi c'est une commande et non un réglage
 *
 * Le délai de restitution n'a de sens que si tout le monde l'applique : si
 * l'un attend et l'autre non, celui qui n'attend pas est en avance sur ses
 * partenaires dans sa propre tête, il ralentit pour les attendre, et le tempo
 * de l'ensemble descend. Le mode se bascule donc au service, qui publie le
 * délai commun pour toute la salle, et tout le monde le pose tel quel.
 * N'importe quel participant peut basculer, comme pour la pause. L'allumer
 * retire le lead : tout le monde émet, il n'y a plus de soliste.
 *
 * `on` part toujours, à vrai comme à faux : éteindre est un geste légitime, et
 * le service refuse (400) un corps sans le champ pour ne pas le confondre avec
 * un oubli.
 *
 * Même discipline d'échec que `sendSessionCommand` : `false` sans lever, motif
 * dans la console. La réponse n'est pas lue non plus — l'état revient par la
 * métadonnée, et c'est elle que l'interrupteur reflète.
 */
export async function sendJamMode(
  clockUrl: string,
  room: string,
  token: string,
  on: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/jam`, token, { on }, "mode Jam", fetchImpl);
}

/**
 * Prend le lead, ou le rend.
 *
 * # Un geste sur soi
 *
 * L'identité vient du jeton, jamais du corps : personne ne désigne quelqu'un
 * d'autre, et on ne peut rendre que le sien. C'est ce qui fait qu'aucun
 * participant ne peut couper le son d'un autre à distance — il peut seulement
 * prendre sa place, et ça se voit dans le panneau.
 *
 * Prendre le lead éteint le jam : les deux régimes s'excluent, et c'est le
 * service qui tient l'exclusion, pas le client. Le rendre laisse le lead
 * vacant, et le prochain qui annonce le prend au tour d'après — c'est le
 * même mécanisme qui le libère quand son détenteur ferme son onglet.
 *
 * `take` part toujours, à vrai comme à faux : rendre est un geste légitime, et
 * le service refuse (400) un corps sans le champ pour ne pas le confondre avec
 * un oubli.
 *
 * Même discipline d'échec que `sendJamMode` : `false` sans lever, motif dans
 * la console, et la réponse n'est pas lue — l'état revient par la métadonnée.
 */
export async function sendLead(
  clockUrl: string,
  room: string,
  token: string,
  take: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/lead`, token, { take }, "lead", fetchImpl);
}

/**
 * Les réglages d'écran d'une séance, en patch : un champ absent ne change
 * rien. Voir `sendSessionSettings`.
 */
export interface SessionSettings {
  /** Masquer les lignes de mesure du rouleau, ou les rétablir. */
  measuresHidden?: boolean;
  /** Baisser le rideau — plus rien ne sort de la scène — ou le relever. */
  curtain?: boolean;
  /** Fermer le chat à la salle — l'interprète y parle encore —, ou le rouvrir. */
  chatClosed?: boolean;
  /**
   * Le cadrage des caméras : la table **entière**, et non une entrée à fondre
   * dans celle qui est en place. Absente, elle ne change rien ; vide, elle
   * efface.
   *
   * Seul réglage qui se remplace au lieu de se fondre, et c'est ce qui
   * l'empêche d'enfler : une table fusionnée garderait l'entrée de chaque
   * appareil qui passe, dans une métadonnée que toute la salle reçoit à chaque
   * changement. Une seule machine en décide — celle de qui pilote —, et elle
   * la tient complète.
   */
  cameras?: CameraFramings;
}

/**
 * Change les réglages d'écran de la séance, pour tout le monde.
 *
 * # Pourquoi le service, et pas chacun chez soi
 *
 * Parce que le geste ne veut rien dire autrement. Masquer les lignes de mesure
 * pendant un récital, c'est décider de ce que le **public** regarde ; réglé
 * chacun de son côté, l'interprète les retirait de son seul écran et la salle
 * continuait de les voir. Une fois publié dans l'état de séance, le réglage
 * atteint aussi celui qui arrive après le geste, sans que personne ait à le lui
 * renvoyer.
 *
 * # Pourquoi une route pour tous les réglages
 *
 * Le régime et le lead gardent la leur : ce sont des faits de jeu, avec leurs
 * exclusions. Un interrupteur d'affichage, non — il n'y en a qu'un aujourd'hui,
 * et le suivant n'ajoutera qu'un champ ici. Le tempo, lui, n'y entre jamais :
 * c'est une commande de transport, qui réancre la droite de toute la salle.
 *
 * Le corps ne porte que ce qu'on change, et le service refuse (400) un corps
 * sans aucun réglage connu : un champ absent vaut « inchangé », jamais
 * « faux ».
 *
 * Même discipline d'échec que `sendJamMode` : `false` sans lever, motif dans la
 * console, et la réponse n'est pas lue — l'état revient par la métadonnée, et
 * c'est elle que l'interrupteur reflète.
 */
export async function sendSessionSettings(
  clockUrl: string,
  room: string,
  token: string,
  settings: SessionSettings,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  const body: Record<string, JsonValue> = {};

  if (settings.measuresHidden !== undefined) {
    body.measures_hidden = settings.measuresHidden;
  }

  if (settings.curtain !== undefined) {
    body.curtain = settings.curtain;
  }

  if (settings.chatClosed !== undefined) {
    body.chat_closed = settings.chatClosed;
  }

  if (settings.cameras !== undefined) {
    body.cameras = framingsBody(settings.cameras);
  }

  return post(clockUrl, `/sessions/${room}/settings`, token, body, "réglages", fetchImpl);
}

/** Ce que rend une annonce. */
export interface PlayoutReply {
  /** Le service a accepté l'annonce. */
  ok: boolean;
  /**
   * Le délai que le service attribue à l'appelant, en millisecondes : zéro
   * pour le leader, son trajet depuis le leader pour un auditeur, le délai
   * commun en jam. `null` sans réponse acceptée, ou sur une réponse illisible.
   */
  delayMs: number | null;
}

/**
 * Annonce ce que cette machine coûte — sa jambe jusqu'au SFU, son
 * ordonnancement audio, son tampon de sortie, le trajet de ses notes — et rend
 * le délai que le service lui attribue en retour.
 *
 * # Ce qu'on annonce, et ce qu'on n'annonce pas
 *
 * Seulement ce que lui seul connaît. Ni le pire trajet de la salle, ni une
 * marge, ni un arrondi : tout ce qui se déduit de l'ensemble des annonces
 * appartient au service, qui est le seul à les voir toutes. Une version
 * précédente diffusait la jambe de pair à pair et laissait chaque client
 * calculer son retard : trois clients tenaient trois versions de la même
 * règle. L'identité vient du jeton, jamais du corps.
 *
 * # Pourquoi la réponse se lit
 *
 * Elle ne se lisait pas : le délai de chacun était publié dans la métadonnée
 * de salle, dans une table que toute la salle recevait à chaque ligne qui
 * bougeait, alors que personne n'a l'usage du délai des autres. Le service le
 * rend maintenant à celui qui annonce, et à lui seul — `{"delay_ms": …}`, rien
 * d'autre. C'est le seul chemin de ce délai hors jam ; en jam, le délai commun
 * se lit dans l'état publié (voir `ownDelayMs`).
 *
 * Ici on lit, sans rien décider : une réponse peut revenir après celle d'une
 * annonce partie plus tard, et c'est à l'appelant de ne retenir que la bonne
 * (voir `PrivateDelay`).
 *
 * # Ce qui ne lève jamais
 *
 * Même discipline d'échec que `sendSessionCommand` : `ok` à faux sans lever,
 * motif dans la console. Une annonce perdue ne casse rien — la suivante part
 * au tour d'après, et le service garde une jambe quelques secondes. Une
 * réponse acceptée mais illisible rend `ok` et un délai `null` : la jambe est
 * arrivée, mais on ne pose pas sur le transport un nombre qu'on n'a pas su
 * lire.
 */
export async function announcePlayout(
  clockUrl: string,
  room: string,
  token: string,
  info: LivePeerAnnouncement,
  fetchImpl: typeof fetch = fetch,
): Promise<PlayoutReply> {
  const response = await request(
    clockUrl,
    `/sessions/${room}/playout`,
    token,
    {
      leg_ms: info.legMs,
      look_ahead_ms: info.lookAheadMs,
      output_ms: info.outputMs,
      transit_ms: info.transitMs,
    },
    "annonce",
    fetchImpl,
  );

  if (response === null) {
    return { ok: false, delayMs: null };
  }

  const body = await readJson(response);

  return { ok: true, delayMs: isRecord(body) ? durationOrNull(body.delay_ms) : null };
}

/**
 * Retire de la salle un appareil qui filme. Rend `false` sans lever.
 *
 * Gardé chez le service : il y faut piloter la séance, ou être le propriétaire
 * de l'appareil, et le service refuse de retirer quiconque n'est pas une
 * caméra. Le téléphone coupé l'apprend par LiveKit, et sa page le dit. Même
 * discipline d'échec que `sendSessionCommand`.
 */
export async function removeLiveCamera(
  clockUrl: string,
  room: string,
  token: string,
  identity: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/cameras/remove`, token, { identity }, "coupure de caméra", fetchImpl);
}

/** Ce qu'un envoi de message devient : parti, refusé pour le débit, ou perdu. */
export type ChatSendOutcome = "sent" | "too-fast" | "muted" | "closed" | "failed";

/**
 * Dit quelque chose à la salle.
 *
 * # Pourquoi le service, et pas le canal de données
 *
 * Parce qu'un spectateur ne peut rien y publier — c'est son jeton qui le dit,
 * et c'est ce qui garantit qu'aucune note ne vient de lui. Le service parle
 * dans la salle à sa place, sous le nom que son jeton porte, et LiveKit le
 * livre à tous. Le message revient donc par la salle, à l'émetteur comme aux
 * autres : la réponse ne porte que l'accusé, et rien n'est affiché sur elle.
 *
 * Rien n'est gardé nulle part : un message est du direct, et qui entre après
 * ne le lira pas.
 *
 * # Cinq issues, pas deux
 *
 * Le débit est borné par personne, et un refus pour ça se dit autrement qu'une
 * panne : « attends un instant » n'est pas « ça ne marche pas ». La parole
 * retirée et le chat fermé sont deux autres refus, et deux 403 que le service
 * distingue par un `code` dans le corps : ils ne s'affichent pas pareil, et
 * le premier fait taire le champ jusqu'à ce que la parole revienne. Ne lève
 * jamais ; motif dans la console, comme pour les commandes.
 */
export async function sendChatMessage(
  clockUrl: string,
  room: string,
  token: string,
  text: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ChatSendOutcome> {
  if (clockUrl === "") {
    return "failed";
  }

  try {
    const response = await fetchImpl(`${clockUrl}/sessions/${room}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ text }),
    });

    if (response.ok) {
      return "sent";
    }

    console.warn("[live] message refusé", response.status);

    if (response.status === 429) {
      return "too-fast";
    }

    if (response.status === 403) {
      const code = await refusalCode(response);

      if (code === "muted") {
        return "muted";
      }

      if (code === "chat_closed") {
        return "closed";
      }
    }

    return "failed";
  } catch (cause) {
    console.warn("[live] message non parti", cause);

    return "failed";
  }
}

/** Le `code` d'un refus, ou `null` : un corps sans code est un refus ordinaire. */
async function refusalCode(response: Response): Promise<string | null> {
  const body = await readJson(response);

  return isRecord(body) && typeof body.code === "string" ? body.code : null;
}

/**
 * Fait oublier un message à toute la salle. Le geste de l'interprète : le
 * service le refuse à tout autre. Rien n'est gardé, donc rien ne se supprime —
 * le retrait revient par la salle, et chaque fil retire la ligne. `false`
 * sans lever, comme les commandes.
 */
export async function retractChatMessage(
  clockUrl: string,
  room: string,
  token: string,
  id: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/chat/retract`, token, { id }, "retrait du message", fetchImpl);
}

/**
 * Retire la parole à quelqu'un, ou la lui rend. La personne est reconnue par
 * son compte d'un onglet à l'autre ; elle et l'interprète l'apprennent par la
 * salle, personne d'autre. Ses cœurs passent encore.
 */
export async function muteParticipant(
  clockUrl: string,
  room: string,
  token: string,
  identity: string,
  muted: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/participants/mute`, token, { identity, muted }, "sourdine", fetchImpl);
}

/**
 * Sort quelqu'un du récital, pour le reste de la séance : LiveKit le
 * déconnecte, et le service le ressort s'il revient. Rien n'en survit à la
 * séance — ne plus entrer au récital suivant serait un fait de compte, qui
 * n'existe pas encore.
 */
export async function excludeParticipant(
  clockUrl: string,
  room: string,
  token: string,
  identity: string,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/participants/exclude`, token, { identity }, "exclusion", fetchImpl);
}

/**
 * Envoie des cœurs à la salle : ce qu'on a tapé depuis le dernier envoi,
 * regroupé sur quelques centaines de millisecondes (voir `ReactionOutbox`).
 *
 * Le service additionne ce que toute la salle envoie et publie un compte à la
 * fois, à sa cadence : rien ne revient ici, et le compte arrive par la salle.
 * Même discipline d'échec que `sendSessionCommand` : `false` sans lever.
 */
export async function sendReactions(
  clockUrl: string,
  room: string,
  token: string,
  count: number,
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  return post(clockUrl, `/sessions/${room}/reactions`, token, { kind: "heart", count }, "réactions", fetchImpl);
}

/** Une ligne de la route des participants. */
export interface LiveParticipantLine {
  /** Son identité dans la salle, celle de son billet. */
  identity: string;
  /** Son nom, tel que son jeton le porte. Son identité s'il n'en a pas. */
  name: string;
  /** Son jeton lui laisse-t-il jouer. Faux pour un spectateur de récital. */
  canPlay: boolean;
  /**
   * Ce qu'il a annoncé et le délai qui lui revient, ou `null` : le service ne
   * rend les mesures qu'à qui peut jouer.
   */
  measures: LivePeerInfo | null;
  /**
   * L'interprète lui a retiré la parole. Le service ne l'écrit qu'à qui
   * pilote : chez un spectateur, toujours faux.
   */
  muted: boolean;
}

/** Ce que rend la route des participants. */
export interface LiveParticipantsReply {
  /**
   * Combien le service voit de participants, soi compris. Le vrai total : la
   * liste s'arrête à cinq cents lignes. `null` si la réponse ne le porte pas.
   */
  audience: number | null;
  /** Ceux qui peuvent jouer d'abord, puis par nom : l'ordre du service. */
  participants: readonly LiveParticipantLine[];
}

/**
 * Qui le service voit dans la séance, et ce que chacun coûte.
 *
 * # Pourquoi une route, et à la demande
 *
 * Les mesures de chacun voyageaient dans la métadonnée de salle, livrées à
 * toute la salle à chaque ligne qui bougeait, alors qu'elles ne servent qu'au
 * panneau de séance. Elles se demandent maintenant, par celui qui a ce panneau
 * ouvert et tant qu'il l'a ouvert (voir `useLiveParticipants`).
 *
 * Tout le monde y lit la liste des noms, spectateurs compris — LiveKit, lui,
 * ne montre plus un spectateur au reste de la salle. Les mesures ne sont
 * rendues qu'à qui peut jouer : un spectateur n'en a pas l'usage, et c'est le
 * service qui en décide, sur le jeton.
 *
 * # Ce qui ne lève jamais
 *
 * Même tolérance que `parseSessionState` : une ligne sans identité est laissée
 * de côté, un champ illisible vaut son défaut. `null` sans lever quand rien ne
 * se lit — service absent, refus, réponse cassée —, avec le motif dans la
 * console comme pour les commandes : le panneau garde alors ce qu'il montrait.
 */
export async function fetchLiveParticipants(
  clockUrl: string,
  room: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LiveParticipantsReply | null> {
  const response = await request(
    clockUrl,
    `/sessions/${room}/participants`,
    token,
    null,
    "liste des participants",
    fetchImpl,
  );

  if (response === null) {
    return null;
  }

  const body = await readJson(response);

  if (!isRecord(body) || !Array.isArray(body.participants)) {
    return null;
  }

  return {
    audience: countOrNull(body.audience),
    participants: body.participants.flatMap((entry: unknown) => {
      const line = readParticipantLine(entry);

      return line === null ? [] : [line];
    }),
  };
}

/** Les champs de mesure d'une ligne, que le service n'écrit que pour qui peut jouer. */
const MEASURE_FIELDS = ["leg_ms", "look_ahead_ms", "output_ms", "transit_ms", "delay_ms"];

/**
 * Lit une ligne de la route des participants, ou rend `null`.
 *
 * Les mesures sont là dès qu'un de leurs champs l'est, et un champ absent vaut
 * zéro : un service qui omettrait les zéros ne doit pas faire passer un joueur
 * pour un spectateur.
 */
function readParticipantLine(entry: unknown): LiveParticipantLine | null {
  if (!isRecord(entry) || typeof entry.identity !== "string" || entry.identity === "") {
    return null;
  }

  const measured = MEASURE_FIELDS.some((field) => field in entry);

  return {
    identity: entry.identity,
    name: typeof entry.name === "string" && entry.name !== "" ? entry.name : entry.identity,
    canPlay: entry.can_play === true,
    muted: entry.muted === true,
    measures: measured
      ? {
          legMs: durationOrNull(entry.leg_ms) ?? 0,
          lookAheadMs: durationOrNull(entry.look_ahead_ms) ?? 0,
          outputMs: durationOrNull(entry.output_ms) ?? 0,
          transitMs: durationOrNull(entry.transit_ms) ?? 0,
          delayMs: durationOrNull(entry.delay_ms) ?? 0,
        }
      : null,
  };
}

/**
 * Ce qu'un corps porte : du JSON, et rien qui ne se sérialise.
 *
 * Ce fut une carte plate de nombres, de chaînes et de booléens — tous les
 * corps du service en étaient. La table de cadrage des caméras est le premier
 * qui ne l'est pas : une entrée par caméra, et un objet par entrée.
 */
type JsonValue = string | number | boolean | null | readonly JsonValue[] | { readonly [key: string]: JsonValue };

/** Un envoi au service, qui ne dit que s'il a été accepté. */
async function post(
  clockUrl: string,
  path: string,
  token: string,
  body: Record<string, JsonValue>,
  what: string,
  fetchImpl: typeof fetch,
): Promise<boolean> {
  return (await request(clockUrl, path, token, body, what, fetchImpl)) !== null;
}

/**
 * Une requête au service, avec le jeton du participant : un envoi quand il y
 * a un corps, une lecture sinon. Rend la réponse quand le service l'a
 * acceptée, `null` sinon, sans jamais lever. `what` nomme la requête dans la
 * console — c'est le seul endroit où le motif d'un échec se connaît.
 */
async function request(
  clockUrl: string,
  path: string,
  token: string,
  body: Record<string, JsonValue> | null,
  what: string,
  fetchImpl: typeof fetch,
): Promise<Response | null> {
  if (clockUrl === "") {
    return null;
  }

  const init: RequestInit =
    body === null
      ? { method: "GET", headers: { Authorization: `Bearer ${token}` } }
      : {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        };

  try {
    const response = await fetchImpl(`${clockUrl}${path}`, init);

    if (!response.ok) {
      console.warn(`[live] ${what} refusée`, response.status);

      return null;
    }

    return response;
  } catch (cause) {
    // Le cas muet par excellence : origine non autorisée, certificat refusé,
    // service injoignable. Le navigateur n'envoie parfois même pas la requête.
    console.warn(`[live] ${what} non partie`, cause);

    return null;
  }
}

/**
 * Le corps d'une réponse acceptée, ou `null` s'il ne se lit pas : elle peut
 * venir d'un service plus ancien, qui ne rendait pas la même chose.
 */
async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * La table de cadrage telle que le service la lit : les noms de champs de la
 * charge, et rien pour une caméra qui ne demande rien.
 *
 * Les entrées neutres sont écartées ici plutôt que laissées au service. Il les
 * écarte aussi — c'est sa table qui fait foi —, mais les envoyer ferait croire
 * à la lecture du corps que quelque chose a été réglé, et le journal du service
 * compterait des caméras cadrées qui ne le sont pas.
 */
function framingsBody(cameras: CameraFramings): Record<string, JsonValue> {
  const body: Record<string, JsonValue> = {};

  for (const [identity, framing] of Object.entries(cameras)) {
    if (isDefaultFraming(framing)) {
      continue;
    }

    body[identity] = {
      rotation: framing.rotation,
      zoom: framing.zoom,
      x: framing.x,
      y: framing.y,
      ...(framing.subject === null ? {} : { subject: framing.subject }),
      ...(framing.lens === null ? {} : { lens: framing.lens }),
    };
  }

  return body;
}
